import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Button,
} from '@mui/material';

import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import Chart from 'react-apexcharts';

import Client from './../Client';
import BackofficeReportsApi from './backofficeReportsApi';

function formatDate(value) {
  if (!value) return '';
  const parts = String(value).substring(0, 10).split('-');
  if (parts.length !== 3) return String(value);
  const [year, month, day] = parts;
  return `${day}/${month}`;
}

function formatNumber(value) {
  const num = Number(value || 0);

  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';

  return num.toFixed(0);
}

class ClientsActivityEvolution extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client('none'),
      data: [],
      period: {},
      metric: 'orders',
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.periodMode !== this.props.periodMode) {
      this.loadData();
    }
  }

  loadData = () => {
    const queryData = {
      periodMode: this.props.periodMode || 'last_30d',
    };

    const data = BackofficeReportsApi.getClientsRegisteredAnonymousEvolution(this.state.client, queryData);

    if (data) {
      this.setState({
        data: data.rows || [],
        period: data.period || {},
      });
    } else {
      this.setState({
        data: [],
        period: {},
      });
    }
  };

  handleMetricChange = (event, newMetric) => {
    if (newMetric !== null) {
      this.setState({ metric: newMetric });
    }
  };

  csvValue = (value) => {
    if (value === null || typeof value === 'undefined') return '""';

    const cleanValue = String(value).replace(/"/g, '""');

    return `"${cleanValue}"`;
  };

  handleExportCsv = () => {
    const rows = this.state.data || [];

    if (!rows.length) {
      return;
    }

    const periodMode = this.props.periodMode || 'last_30d';
    const periodLabel = this.state.period?.label || periodMode;

    const totalRegisteredOrders = rows.reduce(
      (sum, row) => sum + Number(row.registered_orders || 0),
      0
    );

    const totalAnonymousOrders = rows.reduce(
      (sum, row) => sum + Number(row.anonymous_orders || 0),
      0
    );

    const totalRegisteredSales = rows.reduce(
      (sum, row) => sum + Number(row.registered_value || 0),
      0
    );

    const totalAnonymousSales = rows.reduce(
      (sum, row) => sum + Number(row.anonymous_value || 0),
      0
    );

    const totalOrders = totalRegisteredOrders + totalAnonymousOrders;
    const totalSales = totalRegisteredSales + totalAnonymousSales;

    const registeredOrderShare = totalOrders > 0
      ? (totalRegisteredOrders / totalOrders) * 100
      : 0;

    const registeredSalesShare = totalSales > 0
      ? (totalRegisteredSales / totalSales) * 100
      : 0;

    const registeredAov = totalRegisteredOrders > 0
      ? totalRegisteredSales / totalRegisteredOrders
      : 0;

    const anonymousAov = totalAnonymousOrders > 0
      ? totalAnonymousSales / totalAnonymousOrders
      : 0;

    const headers = [
      { key: 'date', label: 'Date' },
      { key: 'registered_orders', label: 'Registered orders' },
      { key: 'anonymous_orders', label: 'Anonymous orders' },
      { key: 'registered_value', label: 'Registered sales' },
      { key: 'anonymous_value', label: 'Anonymous sales' },
    ];

    const csvRows = [
      ['Client activity evolution'].map((value) => this.csvValue(value)).join(','),
      ['Period', periodLabel].map((value) => this.csvValue(value)).join(','),
      ['Period mode', periodMode].map((value) => this.csvValue(value)).join(','),
      ['Generated at', new Date().toLocaleString()].map((value) => this.csvValue(value)).join(','),
      '',
      ['Summary'].map((value) => this.csvValue(value)).join(','),
      ['Total registered orders', totalRegisteredOrders].map((value) => this.csvValue(value)).join(','),
      ['Total anonymous orders', totalAnonymousOrders].map((value) => this.csvValue(value)).join(','),
      ['Total orders', totalOrders].map((value) => this.csvValue(value)).join(','),
      ['Total registered sales', totalRegisteredSales.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Total anonymous sales', totalAnonymousSales.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Total sales', totalSales.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Registered order share %', `${registeredOrderShare.toFixed(1)}%`].map((value) => this.csvValue(value)).join(','),
      ['Registered sales share %', `${registeredSalesShare.toFixed(1)}%`].map((value) => this.csvValue(value)).join(','),
      ['Registered AOV', registeredAov.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      ['Anonymous AOV', anonymousAov.toFixed(2)].map((value) => this.csvValue(value)).join(','),
      '',
      headers.map((header) => this.csvValue(header.label)).join(','),
      ...rows.map((row) =>
        headers
          .map((header) => {
            if (
              header.key === 'registered_value' ||
              header.key === 'anonymous_value'
            ) {
              return this.csvValue(Number(row[header.key] || 0).toFixed(2));
            }

            return this.csvValue(row[header.key]);
          })
          .join(',')
      ),
    ];

    const csvContent = csvRows.join('\n');

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', `client_activity_evolution_${periodMode}.csv`);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  render() {
    const rows = this.state.data || [];
    const isOrders = this.state.metric === 'orders';
    const periodLabel = this.state.period?.label || 'Last 30d';

    const categories = rows.map((row) => formatDate(row.date));

    const series = [
      {
        name: isOrders ? 'Registered orders' : 'Registered sales',
        data: rows.map((row) =>
          Number(isOrders ? row.registered_orders || 0 : row.registered_value || 0)
        ),
      },
      {
        name: isOrders ? 'Anonymous orders' : 'Anonymous sales',
        data: rows.map((row) =>
          Number(isOrders ? row.anonymous_orders || 0 : row.anonymous_value || 0)
        ),
      },
    ];

    const options = {
      chart: {
        type: 'line',
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      markers: {
        size: 0,
      },
      colors: ['#1a237e', '#607d8b'],
      grid: {
        borderColor: 'rgba(15, 23, 42, 0.08)',
      },
      xaxis: {
        categories: categories,
        tickAmount: rows.length > 14 ? 10 : undefined,
        labels: {
          rotate: -35,
          hideOverlappingLabels: true,
          trim: false,
          style: {
            fontSize: '11px',
          },
        },
        tooltip: {
          enabled: false,
        },
      },
      yaxis: {
        labels: {
          formatter: function (value) {
            return formatNumber(value);
          },
          style: {
            fontSize: '11px',
          },
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: function (value) {
            return formatNumber(value);
          },
        },
      },
      legend: {
        position: 'bottom',
        fontSize: '12px',
        markers: {
          width: 9,
          height: 9,
          radius: 9,
        },
      },
      dataLabels: {
        enabled: false,
      },
    };

    return (
      <Card
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: { xs: '14px', sm: '16px' },
          boxShadow: 'none',
          border: 'none',
          overflow: 'hidden',
        }}
      >
        <CardContent
          sx={{
            height: '100%',
            boxSizing: 'border-box',
            p: { xs: 1.5, sm: 2 },
            '&:last-child': { pb: { xs: 1.5, sm: 2 } },
          }}
        >
          <Stack spacing={1.25} sx={{ height: '100%', minHeight: 0 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
              sx={{ flexShrink: 0 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: '#111827',
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    lineHeight: 1.2,
                  }}
                >
                  Client activity evolution
                </Typography>

                <Typography
                  color="text.secondary"
                  sx={{
                    mt: 0.25,
                    fontSize: { xs: '0.76rem', sm: '0.82rem' },
                  }}
                >
                  Registered vs anonymous activity · {periodLabel}
                </Typography>
              </Box>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  alignSelf: { xs: 'stretch', sm: 'center' },
                  maxWidth: '100%',
                  overflowX: { xs: 'auto', sm: 'visible' },
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadRoundedIcon />}
                  disabled={!rows.length}
                  onClick={this.handleExportCsv}
                  sx={{
                    flexShrink: 0,
                    height: 32,
                    minWidth: 64,
                    px: 1,
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 800,
                    fontSize: { xs: '0.68rem', sm: '0.75rem' },
                    whiteSpace: 'nowrap',
                    color: '#111827',
                    borderColor: 'rgba(15, 23, 42, 0.14)',
                    bgcolor: '#ffffff',
                    '&:hover': {
                      bgcolor: '#f8fafc',
                      borderColor: 'rgba(15, 23, 42, 0.24)',
                    },
                    '& .MuiButton-startIcon': {
                      mr: 0.5,
                      ml: 0,
                    },
                  }}
                >
                  CSV
                </Button>

                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={this.state.metric}
                  onChange={this.handleMetricChange}
                  sx={{
                    flexShrink: 0,
                    '& .MuiToggleButton-root': {
                      px: { xs: 1, sm: 1.25 },
                      py: 0.35,
                      fontSize: { xs: '0.72rem', sm: '0.78rem' },
                      fontWeight: 700,
                      textTransform: 'none',
                    },
                  }}
                >
                  <ToggleButton value="orders">Orders</ToggleButton>
                  <ToggleButton value="sales">Sales</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Stack>

            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
              <Chart
                options={options}
                series={series}
                type="line"
                height="100%"
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  }
}

export default ClientsActivityEvolution;
